import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, FileDown, Trash2 } from "lucide-react";
import Flag from "../../components/Flag";
import { countryName } from "../../lib/countries";

const fmt = (s) => `${(s || 0).toFixed(1)}s`;

export default function AdminResults() {
  const { quizId } = useParams();
  const [items, setItems] = useState([]);
  const [limit, setLimit] = useState("all");
  const [selected, setSelected] = useState(new Set());

  const load = () => api.get(`/admin/quizzes/${quizId}/results`).then((r) => { setItems(r.data || []); setSelected(new Set()); });
  useEffect(() => { load(); }, [quizId]);

  const quizName = items[0]?.quiz_name_pt || "";
  const displayed = limit === "all" ? items : items.slice(0, parseInt(limit, 10));

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) => (prev.size === displayed.length ? new Set() : new Set(displayed.map((r) => r.id))));
  };

  const deleteOne = async (id) => {
    if (!window.confirm("Eliminar este resultado? Esta ação é irreversível.")) return;
    try { await api.delete(`/admin/results/${id}`); toast.success("Resultado eliminado"); load(); }
    catch (e) { toast.error("Erro ao eliminar"); }
  };
  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Eliminar ${selected.size} resultado(s) selecionado(s)?`)) return;
    try { await api.post(`/admin/results/delete-bulk`, { ids: Array.from(selected) }); toast.success("Resultados eliminados"); load(); }
    catch (e) { toast.error("Erro ao eliminar"); }
  };
  const deleteAll = async () => {
    if (items.length === 0) return;
    if (!window.confirm("Eliminar TODOS os resultados deste concurso? Esta ação é irreversível.")) return;
    try { await api.delete(`/admin/quizzes/${quizId}/results`); toast.success("Todos os resultados eliminados"); load(); }
    catch (e) { toast.error("Erro ao eliminar"); }
  };

  const exportCSV = () => {
    const headers = ["pos", "quiz", "username", "name", "school", "nationality", "email", "resolution", "penalty", "final"];
    const rows = displayed.map((r, i) => [
      i + 1,
      r.quiz_name_pt || "",
      r.username || "",
      r.participant_name || "",
      r.school || "",
      countryName(r.nationality, "pt") || r.nationality || "",
      r.email || "",
      r.resolution_seconds,
      r.penalty_seconds,
      r.final_seconds,
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `results-${quizId}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-results-page">
      <Link to="/admin/quizzes" className="inline-flex items-center gap-1 text-sm text-[#0B1D3A]/60 mb-4"><ArrowLeft size={14}/> Voltar</Link>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A]">Resultados</h1>
          {quizName && <p className="text-sm text-[#0B1D3A]/60 mt-1" data-testid="results-quiz-name">Quiz: <span className="font-semibold text-[#005F73]">{quizName}</span></p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={exportCSV} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0B1D3A] text-white font-semibold" data-testid="results-export-csv-btn"><Download size={16}/> Exportar CSV</button>
          <button onClick={deleteAll} disabled={items.length === 0} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white font-semibold disabled:opacity-40" data-testid="results-delete-all-btn"><Trash2 size={16}/> Eliminar todos</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <span className="text-sm text-[#0B1D3A]/60">Mostrar:</span>
        <select value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="results-limit-select" className="px-3 py-1.5 rounded-lg border border-[#0B1D3A]/15 text-sm bg-white">
          <option value="10">Top 10</option>
          <option value="25">Top 25</option>
          <option value="50">Top 50</option>
          <option value="all">Todos</option>
        </select>
        <span className="text-sm text-[#0B1D3A]/50" data-testid="results-count-label">{displayed.length} de {items.length}</span>
        {selected.size > 0 && (
          <button onClick={deleteSelected} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-red-600 text-white text-sm font-semibold" data-testid="results-delete-selected-btn">
            <Trash2 size={14}/> Eliminar selecionados ({selected.size})
          </button>
        )}
      </div>

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-hidden">
        <table className="w-full compact-table">
          <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
            <tr>
              <th className="px-4 py-3"><input type="checkbox" checked={displayed.length > 0 && selected.size === displayed.length} onChange={toggleAll} data-testid="results-select-all"/></th>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Utilizador</th>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Escola</th>
              <th className="text-left px-4 py-3">Nacionalidade</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-right px-4 py-3">Resolução</th>
              <th className="text-right px-4 py-3">Penalizações</th>
              <th className="text-right px-4 py-3">Final</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {displayed.map((r, i) => (
              <tr key={r.id} className="border-t border-[#0B1D3A]/5" data-testid={`results-row-${i}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggle(r.id)} data-testid={`results-select-${i}`}/></td>
                <td className="px-4 py-3 font-bold">{i + 1}</td>
                <td className="px-4 py-3 font-mono text-[#0B1D3A]/70" data-testid={`results-username-${i}`}>{r.username || "—"}</td>
                <td className="px-4 py-3" data-testid={`results-name-${i}`}>{r.participant_name}</td>
                <td className="px-4 py-3" data-testid={`results-school-${i}`}>{r.school}</td>
                <td className="px-4 py-3" data-testid={`results-nationality-${i}`}>{r.nationality ? <Flag code={r.nationality} showName /> : "—"}</td>
                <td className="px-4 py-3" data-testid={`results-email-${i}`}>{r.email}</td>
                <td className="px-4 py-3 text-right">{r.timed_out ? "—" : fmt(r.resolution_seconds)}</td>
                <td className="px-4 py-3 text-right">{r.timed_out ? "—" : fmt(r.penalty_seconds)}</td>
                <td className="px-4 py-3 text-right font-bold text-[#005F73]">{r.timed_out ? <span className="text-[#9CA3AF] font-semibold">Tempo esgotado</span> : fmt(r.final_seconds)}</td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/diploma/${r.id}?lang=pt`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-[#FFD93D]/40 text-[#856404] hover:bg-[#FFD93D]/60"><FileDown size={12}/> PDF</a>
                  <button onClick={() => deleteOne(r.id)} className="ml-2 inline-flex items-center text-red-600 hover:text-red-700" title="Eliminar resultado" data-testid={`results-delete-${i}`}><Trash2 size={14}/></button>
                </td>
              </tr>
            ))}
            {displayed.length === 0 && <tr><td colSpan="11" className="px-4 py-8 text-center text-[#0B1D3A]/50">Sem participações ainda.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
